import {
  type SanityGeneralSettings,
  type SanitySiteFragment,
} from '@data/sanity/queries/types/site'
import { type SanityVideo } from '@data/sanity/queries/types/video'
import {
  type ReactNode,
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react'

type MegaNavUpdateState = boolean | 'toggle'

interface SiteContextMegaNavigation {
  isOpen: boolean
  activeId?: string
}

interface SiteContextVideoModal {
  isOpen: boolean
  video?: SanityVideo
}

interface SiteContextMobileMenu {
  isOpen: boolean
}

interface SiteContextProviderProps {
  site: SanitySiteFragment
  children: ReactNode
}

interface UIContextProps {
  isRouteChanging: boolean
  megaNavigation: SiteContextMegaNavigation
  mobileMenu: SiteContextMobileMenu
  videoModal: SiteContextVideoModal
  toggleIsRouteChanging: (newState: boolean) => void
  toggleMegaNavigation: (updateState: MegaNavUpdateState, id?: string) => void
  toggleMobileMenu: (isOpen: boolean) => void
  toggleVideoModal: (isOpen: boolean, video?: SanityVideo) => void
}

interface LayoutContextProps {
  headerHeight: number
  promoHeight: number
  promoVisibleHeight: number
  breadcrumbsHeight: number
  setHeaderHeight: (height: number) => void
  setPromoHeight: (height: number) => void
  setPromoVisibleHeight: (height: number) => void
  setBreadcrumbsHeight: (height: number) => void
}

interface SiteContextProps {
  settings: SanityGeneralSettings | null
  getProductCount: (collectionSlug?: string) => number
}

const initialLayoutContext: LayoutContextProps = {
  headerHeight: 0,
  promoHeight: 0,
  promoVisibleHeight: 0,
  breadcrumbsHeight: 0,
  setHeaderHeight: () => null,
  setPromoHeight: () => null,
  setPromoVisibleHeight: () => null,
  setBreadcrumbsHeight: () => null,
}

const initialUIContext: UIContextProps = {
  isRouteChanging: false,
  megaNavigation: { isOpen: false },
  mobileMenu: { isOpen: false },
  videoModal: { isOpen: false },
  toggleIsRouteChanging: () => null,
  toggleMegaNavigation: () => null,
  toggleMobileMenu: () => null,
  toggleVideoModal: () => null,
}

const initialSiteContext: SiteContextProps = {
  settings: null,
  getProductCount: () => 0,
}

export const LayoutContext =
  createContext<LayoutContextProps>(initialLayoutContext)

export const UIContext = createContext<UIContextProps>(initialUIContext)

export const SiteContext = createContext<SiteContextProps>(initialSiteContext)

export const SiteContextProvider = ({
  site,
  children,
}: SiteContextProviderProps) => {
  // Layout state
  const [headerHeight, setHeaderHeight] = useState(0)
  const [promoHeight, setPromoHeight] = useState(0)
  const [promoVisibleHeight, setPromoVisibleHeight] = useState(0)
  const [breadcrumbsHeight, setBreadcrumbsHeight] = useState(0)

  // UI state
  const [isRouteChanging, setIsRouteChanging] = useState(false)
  const [mobileMenu, setMobileMenu] = useState<SiteContextMobileMenu>(
    initialUIContext.mobileMenu,
  )
  const [megaNavigation, setMegaNavigation] =
    useState<SiteContextMegaNavigation>(initialUIContext.megaNavigation)
  const [videoModal, setVideoModal] = useState<SiteContextVideoModal>(
    initialUIContext.videoModal,
  )

  // UI callbacks
  const toggleIsRouteChanging = useCallback((newState: boolean) => {
    setIsRouteChanging(newState)
  }, [])

  const toggleMegaNavigation = useCallback(
    (updateState: MegaNavUpdateState, id?: string) => {
      setMegaNavigation((prev) => {
        const isOpen = updateState === 'toggle' ? !prev.isOpen : updateState
        const activeId = isOpen ? id : undefined

        if (prev.isOpen === isOpen && prev.activeId === activeId) {
          return prev
        }

        return { isOpen, activeId }
      })
    },
    [],
  )

  const toggleMobileMenu = useCallback((isOpen: boolean) => {
    setMobileMenu((prev) => {
      if (prev.isOpen === isOpen) {
        return prev
      }

      return { isOpen }
    })

    if (typeof window !== 'undefined') {
      document.body.classList.toggle('overflow-hidden', isOpen)
    }
  }, [])

  const toggleVideoModal = useCallback(
    (isOpen: boolean, video?: SanityVideo) => {
      setVideoModal({
        isOpen,
        video,
      })
    },
    [],
  )

  // Memoized context values
  const layoutValue = useMemo(() => {
    const layoutValue: LayoutContextProps = {
      headerHeight,
      promoHeight,
      promoVisibleHeight,
      breadcrumbsHeight,
      setHeaderHeight,
      setPromoHeight,
      setPromoVisibleHeight,
      setBreadcrumbsHeight,
    }

    return layoutValue
  }, [headerHeight, promoHeight, promoVisibleHeight, breadcrumbsHeight])

  const uiValue = useMemo(() => {
    const uiValue: UIContextProps = {
      isRouteChanging,
      megaNavigation,
      mobileMenu,
      videoModal,
      toggleIsRouteChanging,
      toggleMegaNavigation,
      toggleMobileMenu,
      toggleVideoModal,
    }

    return uiValue
  }, [
    isRouteChanging,
    megaNavigation,
    mobileMenu,
    videoModal,
    toggleIsRouteChanging,
    toggleMegaNavigation,
    toggleMobileMenu,
    toggleVideoModal,
  ])

  const siteValue = useMemo(() => {
    const siteValue: SiteContextProps = {
      settings: site.generalSettings,
      getProductCount: (collectionSlug?: string) => {
        const productCount = site.productCounts?.find((productCount) => {
          return productCount.slug === collectionSlug
        })

        return productCount?.count ?? 0
      },
    }

    return siteValue
  }, [site])

  return (
    <LayoutContext.Provider value={layoutValue}>
      <UIContext.Provider value={uiValue}>
        <SiteContext.Provider value={siteValue}>
          {children}
        </SiteContext.Provider>
      </UIContext.Provider>
    </LayoutContext.Provider>
  )
}
